import React from 'react';
import { Moment } from 'moment';
import { Flex } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
	CargoLimitingFactor,
	DraftUnits,
	EstimateStatus,
	PortActionTypes,
	SalinityDensities,
	SalinityTypes,
	ShortPortActionTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatQuantity } from '@shared/utils/string';
import type { Port } from '@api/utils/ports';
import { usePortRotationProvider } from '@client/screens/estimates/details/context/PortRotationContext';
import {
	LocalPortRotation,
	LocalPortRotationWithKey,
} from '@client/screens/estimates/details/context/hooks/usePortRotationHandlers';
import NumericInput from '@client/components/NumericInput';
import Select from '@client/components/Select';
import Table from '@client/components/Table/Table';
import TooltipIcon from '@client/components/TooltipIcon';
import { useEstimate } from '@client/screens/estimates/details/context/EstimatorContext';
import { useEstimateChangesProvider } from '@client/screens/estimates/details/context/EstimateChangesContext';
import { renderWrappingTime } from '@client/screens/estimates/details/helpers/portRotationSharedColumns';
import sharedStyles from '@client/screens/estimates/details/styles/shared.module.css';
import styles from '../CommissionsCard/BrokerCommissionDetails.module.css';
import {
	getDensity,
	getDepartureRob,
} from './helpers/intakePortValues';

const { UTC } = TimeZoneFormat;

const SalinityLabels: Record<SalinityTypes, string> = {
	[SalinityTypes.SALT]: 'Salt',
	[SalinityTypes.BRACKISH]: 'Brackish',
	[SalinityTypes.FRESH]: 'Fresh',
};

const salinityOptions = [SalinityTypes.SALT, SalinityTypes.BRACKISH, SalinityTypes.FRESH]
	.map((salinity) => ({
		label: `${SalinityLabels[salinity]} - ${SalinityDensities[salinity]}`,
		value: salinity,
	}));

const draftUnitOptions = Object.values(DraftUnits).map((unit) => ({
	label: unit,
	value: unit,
}));

const LimitingFactorLabels: Record<CargoLimitingFactor, string> = {
	[CargoLimitingFactor.DRAFT]: 'Draft',
	[CargoLimitingFactor.VOLUME]: 'Volume',
};

const OUTLINE = '2px solid #adc6ff';

type OutlineEdge = 'start' | 'end';

const getOutlineEdgeStyle = (edge?: OutlineEdge) => ({
	...(edge === 'start' ? { borderLeft: OUTLINE } : {}),
	...(edge === 'end' ? { borderRight: OUTLINE } : {}),
});

const getHighlightedHeaderCell = (edge?: OutlineEdge) => () => ({
	style: {
		borderTop: OUTLINE,
		...getOutlineEdgeStyle(edge),
	},
});

const getOutlinedCellStyle = (rowIndex: number | undefined, rowCount: number, edge?: OutlineEdge) => ({
	...getOutlineEdgeStyle(edge),
	...(rowIndex === rowCount - 1 ? { borderBottom: OUTLINE } : {}),
});

const exceedsPortMax = (cargoOnboard: number | null | undefined, portMax: number | null | undefined) => (
	cargoOnboard != null && portMax != null && cargoOnboard > portMax
);

const IntakeCalculatorPortRotation = ({
	portRotation,
}: {
	portRotation: LocalPortRotationWithKey[];
}) => {
	const { onPortRotationRowChange } = usePortRotationProvider();
	const { immediateSaveChanges } = useEstimateChangesProvider();
	const {
		estimate: estimateResult,
	} = useEstimate();

	const onCargoQuantityChange = (id: number | undefined, cargoQuantity: number | null) => {
		if (!id) {
			return;
		}

		onPortRotationRowChange(id, {
			cargoQuantity,
			qty: cargoQuantity,
		} as LocalPortRotation);
	};

	const onClearCargoQuantity = (id: number | undefined) => immediateSaveChanges(() => {
		onCargoQuantityChange(id, null);
	});

	const disabled = estimateResult?.status === EstimateStatus.Fixed;
	const vessel = estimateResult?.vessel;

	return (
		<div className={styles.container}>
			<Table
				bordered
				pagination={false}
				size="small"
				dataSource={portRotation}
				scroll={{ x: 'max-content' }}
				data-testid="intakeCalculatorPortRotation"
				columns={[
					{
						title: 'Type',
						dataIndex: 'type',
						onCell: () => ({ className: sharedStyles.calculated }),
						render: (p: string) => ShortPortActionTypes[p as keyof typeof ShortPortActionTypes] ??
							p,
					},
					{
						title: 'Port',
						dataIndex: 'port',
						width: 100,
						onCell: () => ({ className: sharedStyles.calculated }),
						render: (port: Port) => port?.name,
					},
					{
						title: 'Arrival',
						dataIndex: 'arrivalDate',
						onCell: () => ({ className: sharedStyles.calculated }),
						render: (arrivalDate: Moment) => renderWrappingTime(arrivalDate, UTC) ?? '-',
					},
					{
						title: 'Departure',
						dataIndex: 'departureDate',
						onCell: () => ({ className: sharedStyles.calculated }),
						render: (departureDate: Moment) => renderWrappingTime(departureDate, UTC) ?? '-',
					},
					{
						title: 'Draft limit',
						dataIndex: 'Draft',
						width: 130,
						render: (_, record: LocalPortRotationWithKey) => {
							return (
								<NumericInput
									disabled={disabled}
									value={record.draft}
									addonAfter={(
										<Select<DraftUnits>
											disabled={disabled}
											variant="borderless"
											size="small"
											style={{ width: 64 }}
											value={record.draftUnit ?? DraftUnits.METERS}
											options={draftUnitOptions}
											onChange={(draftUnit) => {
												if (!record.id || draftUnit == null) {
													return;
												}

												onPortRotationRowChange(record.id, {
													draftUnit,
												} as LocalPortRotation);
											}}
										/>
									)}
									variant="borderless"
									onChange={(newValue: number | null) => {
										if (!record.id) {
											return;
										}

										onPortRotationRowChange(record.id, {
											draft: newValue == null ?
												null :
												newValue,
										} as LocalPortRotation);
									}}
								/>
							);
						},
					},
					{
						title: 'Salinity',
						dataIndex: 'salinity',
						width: 120,
						render: (_, record: LocalPortRotationWithKey) => (
							<Select<SalinityTypes>
								disabled={disabled}
								variant="borderless"
								size="small"
								fullWidth
								value={record.salinity ?? SalinityTypes.SALT}
								options={salinityOptions}
								onChange={(salinity) => {
									if (!record.id || salinity == null) {
										return;
									}

									onPortRotationRowChange(record.id, {
										salinity,
										density: SalinityDensities[salinity],
									} as LocalPortRotation);
								}}
							/>
						),
					},
					{
						title: 'Density',
						dataIndex: 'density',
						width: 70,
						render: (_, record: LocalPortRotationWithKey) => (
							<NumericInput
								asAsync
								disabled={disabled}
								value={getDensity(record)}
								variant="borderless"
								precision={0}
								separateThousands={false}
								allowNegative={false}
								onChange={(density) => {
									if (!density || Number.isNaN(density)) {
										return;
									}

									onPortRotationRowChange(record.id, {
										density,
									} as LocalPortRotation);
								}}
							/>
						),
					},
					{
						title: 'Adj. draft limit (m)',
						dataIndex: 'adjustedDraftLimit',
						align: 'right',
						width: 80,
						onCell: () => ({ className: sharedStyles.calculatedNumber }),
						render: (adjustedDraftLimit: number | null | undefined) => (
							adjustedDraftLimit != null ?
								formatNumber(adjustedDraftLimit, { separateThousands: true }, 2, 2) :
								'-'
						),
					},
					{
						title: 'ROBs on depart. (MT)',
						key: 'robsOnDeparture',
						align: 'right',
						width: 80,
						onCell: () => ({ className: sharedStyles.calculatedNumber }),
						render: (_, record: LocalPortRotationWithKey) => {
							const departureRob = getDepartureRob(record);
							const isBelowZero = departureRob != null && departureRob < 0;
							const displayedRob = departureRob == null || isBelowZero ? 0 : departureRob;

							return (
								<>
									{formatNumber(displayedRob, { separateThousands: true }, 3)}
									{isBelowZero && (
										<TooltipIcon
											iconProps={{ style: { color: 'red' } }}
											Icon={ExclamationCircleOutlined}
										>
											Forecast bunkers fall below zero at this port
										</TooltipIcon>
									)}
								</>
							);
						},
					},
					{
						title: 'Constants (MT)',
						dataIndex: 'constants',
						width: 120,
						render: (_, record: LocalPortRotationWithKey) => (
							<NumericInput
								asAsync
								disabled={disabled}
								value={record.constants ?? estimateResult?.constantsOverride ?? vessel?.constants}
								variant="borderless"
								onChange={(newValue: number | null) => {
									if (!record.id) {
										return;
									}

									onPortRotationRowChange(record.id, {
										constants: newValue,
									} as LocalPortRotation);
								}}
							/>
						),
					},
					{
						title: 'Port max intake (MT)',
						dataIndex: 'portMax',
						align: 'right',
						width: 100,
						onCell: () => ({ className: sharedStyles.calculatedNumber }),
						render: (portMax: number | null | undefined) => (
							portMax != null ?
								formatNumber(portMax, { separateThousands: true }, 0) :
								'-'
						),
					},
					{
						title: 'Limiting factor',
						dataIndex: 'limitingFactor',
						width: 80,
						onCell: () => ({ className: sharedStyles.calculated }),
						render: (limitingFactor: CargoLimitingFactor | null | undefined) => (
							limitingFactor != null ? LimitingFactorLabels[limitingFactor] : '-'
						),
					},
					{
						title: 'Cargo onboard on arrival (MT)',
						onHeaderCell: getHighlightedHeaderCell('start'),
						dataIndex: 'cargoOnboardOnArrival',
						align: 'right',
						width: 100,
						onCell: (_, rowIndex) => ({
							className: sharedStyles.calculatedNumber,
							style: getOutlinedCellStyle(rowIndex, portRotation.length, 'start'),
						}),
						render: (cargoOnboardOnArrival: number | null | undefined, record: LocalPortRotationWithKey) => (
							<Flex gap={4} justify="flex-end" align="center">
								{record.type !== PortActionTypes.LOADING && exceedsPortMax(cargoOnboardOnArrival, record.portMax) && (
									<TooltipIcon
										iconProps={{ style: { color: 'red' } }}
										Icon={ExclamationCircleOutlined}
										marginLeft={0}
									>
										The total cargo onboard exceeds this port&apos;s max intake
									</TooltipIcon>
								)}
								{cargoOnboardOnArrival != null ? formatQuantity(cargoOnboardOnArrival) : '-'}
							</Flex>
						),
					},
					{
						title: 'Cargo loaded (MT)',
						onHeaderCell: getHighlightedHeaderCell(),
						dataIndex: 'cargoLoaded',
						align: 'right',
						width: 130,
						onCell: (record: LocalPortRotationWithKey, rowIndex) => ({
							className: record.type !== PortActionTypes.LOADING ? sharedStyles.calculatedNumber : undefined,
							style: getOutlinedCellStyle(rowIndex, portRotation.length),
						}),
						render: (cargoLoaded: number | null | undefined, record: LocalPortRotationWithKey) => {
							if (record.type !== PortActionTypes.LOADING) {
								return cargoLoaded != null ? formatQuantity(cargoLoaded) : '-';
							}

							return (
								<NumericInput
									asAsync
									disabled={disabled}
									allowClear={record.cargoQuantity != null}
									suffix={<span />}
									onClear={() => onClearCargoQuantity(record.id)}
									value={record.cargoQuantity ?? cargoLoaded}
									variant="borderless"
									onChange={(newValue: number | null) => onCargoQuantityChange(record.id, newValue)}
								/>
							);
						},
					},
					{
						title: 'Cargo discharged (MT)',
						onHeaderCell: getHighlightedHeaderCell(),
						dataIndex: 'cargoDischarged',
						align: 'right',
						width: 130,
						onCell: (record: LocalPortRotationWithKey, rowIndex) => ({
							className: record.type !== PortActionTypes.DISCHARGING ? sharedStyles.calculatedNumber : undefined,
							style: getOutlinedCellStyle(rowIndex, portRotation.length),
						}),
						render: (cargoDischarged: number | null | undefined, record: LocalPortRotationWithKey) => {
							if (record.type !== PortActionTypes.DISCHARGING) {
								return cargoDischarged != null ? formatQuantity(cargoDischarged) : '-';
							}

							return (
								<NumericInput
									asAsync
									disabled={disabled}
									allowClear={record.cargoQuantity != null}
									suffix={<span />}
									onClear={() => onClearCargoQuantity(record.id)}
									value={record.cargoQuantity ?? cargoDischarged}
									variant="borderless"
									onChange={(newValue: number | null) => onCargoQuantityChange(record.id, newValue)}
								/>
							);
						},
					},
					{
						title: 'Cargo onboard on departure (MT)',
						onHeaderCell: getHighlightedHeaderCell('end'),
						dataIndex: 'cargoOnboardOnDeparture',
						align: 'right',
						width: 100,
						onCell: (_, rowIndex) => ({
							className: sharedStyles.calculatedNumber,
							style: getOutlinedCellStyle(rowIndex, portRotation.length, 'end'),
						}),
						render: (cargoOnboardOnDeparture: number | null | undefined, record: LocalPortRotationWithKey) => (
							<Flex gap={4} justify="flex-end" align="center">
								{record.type === PortActionTypes.LOADING && exceedsPortMax(cargoOnboardOnDeparture, record.portMax) && (
									<TooltipIcon
										iconProps={{ style: { color: 'red' } }}
										Icon={ExclamationCircleOutlined}
										marginLeft={0}
									>
										The total cargo onboard exceeds this port&apos;s max intake
									</TooltipIcon>
								)}
								{cargoOnboardOnDeparture != null ? formatQuantity(cargoOnboardOnDeparture) : '-'}
							</Flex>
						),
					},
				]}
			/>
		</div>
	);
};

export default IntakeCalculatorPortRotation;
